/**
 * Hook for batch-fetching barometer data for multiple calls.
 * Collects district codes from all visible calls with LABOUR_BAROMETER capability,
 * fetches barometer data once, and provides per-call lookup.
 *
 * @see docs/specifications/2026-03-06-kfs-barometr-zawodow.md
 */
import { useMemo } from "react";
import { useBarometer } from "./useBarometer";
import { CallItem, BarometerResponse } from "../types/grants";
import { hasBarometerCapability, getDistrictCodes } from "../components/grants/calls/barometer/barometerUtils";

/** Barometer data for a single call (filtered by its district codes) */
export interface CallBarometerData {
  data: BarometerResponse | undefined;
  isLoading: boolean;
  isError: boolean;
}

/**
 * Collects all unique district codes from calls with LABOUR_BAROMETER capability.
 * Uses Array.from(new Set(...)) pattern per project convention.
 */
function collectDistrictCodes(calls: CallItem[]): string[] {
  const allCodes: string[] = [];
  for (const call of calls) {
    if (hasBarometerCapability(call.program)) {
      const codes = getDistrictCodes(call);
      allCodes.push(...codes);
    }
  }
  return Array.from(new Set(allCodes));
}

/**
 * Filters barometer response to only include districts matching given codes.
 */
function filterBarometerForCall(
  data: BarometerResponse,
  districtCodes: string[]
): BarometerResponse {
  const codeSet = new Set(districtCodes);
  return {
    year: data.year,
    districts: data.districts.filter((d) => codeSet.has(d.code)),
    voivodeship: data.voivodeship,
  };
}

/**
 * Hook that batch-fetches barometer data for all visible calls.
 * Returns a function to get barometer data for a specific call.
 *
 * @param calls - Array of visible calls
 * @returns Object with getCallBarometer function and loading/error state
 */
export function useBarometerBatch(calls: CallItem[]) {
  // Collect all unique district codes from barometer-capable calls
  const allDistrictCodes = useMemo(() => collectDistrictCodes(calls), [calls]);

  // Single batch fetch for all district codes
  const { data, isLoading, isError } = useBarometer(allDistrictCodes);

  // Function to get barometer data for a specific call
  const getCallBarometer = useMemo(() => {
    return (call: CallItem): CallBarometerData => {
      if (!hasBarometerCapability(call.program)) {
        return { data: undefined, isLoading: false, isError: false };
      }

      const codes = getDistrictCodes(call);
      if (codes.length === 0) {
        return { data: undefined, isLoading: false, isError: false };
      }

      if (!data) {
        return { data: undefined, isLoading, isError };
      }

      const filtered = filterBarometerForCall(data, codes);
      return { data: filtered, isLoading: false, isError: false };
    };
  }, [data, isLoading, isError]);

  return {
    getCallBarometer,
    hasBarometerCalls: allDistrictCodes.length > 0,
    isLoading,
    isError,
  };
}
